import queryClient from "infrastructure/reactQuery";
import { useContext } from "react";
import { useInfiniteQuery } from "react-query";
import { IReviewsInput, IReviewsOutput } from "../../../api-schema/reviews";
import { AuthContext } from "../contexts/AuthProvider";
import { SimpleDict } from "../utils/typeHelper";
import { getRequest } from "./apiClient";

const buildReviewCacheKey = ({ bId, category, evaluate }: IReviewsInput) => [
  bId,
  category,
  evaluate,
  "Reviews",
];

const getReviews = async (
  { bId, page, category, evaluate }: IReviewsInput,
  token: string
): Promise<IReviewsOutput> => {
  let params = { category, bId } as SimpleDict;
  if (page !== undefined) {
    params = { ...params, page };
  }
  if (evaluate !== undefined) {
    params = { ...params, evaluate };
  }

  const result = await getRequest<IReviewsOutput>("reviews", params, token);

  return {
    page: result.page,
    reviews: result.reviews.map((review) => {
      return {
        ...review,
        time: new Date(review.time),
      };
    }),
    existMore: result.existMore,
  };
};

const useReviewsData = (reviewsInput: IReviewsInput) => {
  type NextPageParam = { page: number };
  const { token } = useContext(AuthContext);
  const { data, isLoading, isError, fetchNextPage } =
    useInfiniteQuery<IReviewsOutput>(
      buildReviewCacheKey(reviewsInput),
      async ({ pageParam = { page: 1 } }: { pageParam?: NextPageParam }) => {
        return await getReviews(
          { ...reviewsInput, page: pageParam.page },
          token
        );
      },
      {
        getNextPageParam: (lastPage): NextPageParam => {
          const nextPageParam = { page: lastPage.page + 1 };
          return nextPageParam;
        },
      }
    );

  return { data, isLoading, isError, fetchNextPage };
};

export const usePrefetchReviews = (bId: string) => {
  const reviewsInput: IReviewsInput = {
    bId,
    evaluate: "all",
    category: "all",
    page: 1,
  };
  const { token } = useContext(AuthContext);

  const prefetchFunc = () =>
    queryClient.prefetchInfiniteQuery(buildReviewCacheKey(reviewsInput), () =>
      getReviews(reviewsInput, token)
    );
  return prefetchFunc;
};

export default useReviewsData;
